import axios from "axios";
import { notification } from "antd";
import Auth from "./auth.service";
import Storage from "./storage.service";
import Notification from "./Notification";
import { authUrl } from "../constants/apiBaseUrl";

// Request interceptor — attach access token + active-store header if present.
export const requestInterceptor = (request) => {
  if (Auth.token) {
    request.headers.Authorization = `Bearer ${Auth.token}`;
  }
  const storeId = Auth.activeStoreId;
  if (storeId != null && request.headers && request.headers["X-Store-Id"] == null) {
    request.headers["X-Store-Id"] = String(storeId);
  }
  return request;
};

// Cross-request refresh-token rotation: a single in-flight promise so that a
// burst of 401s only triggers one /auth/refresh call.
let refreshInFlight = null;
function refreshAccessToken() {
  if (!Auth.refreshToken) return Promise.resolve(null);
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = axios
    .post(authUrl.refresh_token, { refreshToken: Auth.refreshToken })
    .then((res) => {
      const payload = res?.data?.data;
      if (!payload?.accessToken || !payload?.refreshToken) return null;
      Auth.token = payload.accessToken;
      Auth.refreshToken = payload.refreshToken;
      return payload.accessToken;
    })
    .catch((err) => {
      handleRefreshFailure(err);
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

function handleRefreshFailure(err) {
  const body = err?.response?.data?.data;
  const code = body?.code || err?.response?.data?.code;
  if (code === "AUTH_019") {
    notification.warning({
      message: "Signed out",
      description: "You have been signed out for security reasons.",
      duration: 5,
    });
  }
  Auth.logout();
  if (typeof window !== "undefined" && window.location?.pathname !== "/login") {
    window.location.replace("/login");
  }
}

// A 403 with no business envelope (no message/code/data) is a filter-level
// auth rejection, not a permission-denied response from the app — treat it as
// refresh-eligible (belt-and-suspenders alongside the backend 401 entry point).
// A genuine permission-denied 403 carries a Response<T> body and is left alone.
function isBodylessForbidden(error) {
  if (error?.response?.status !== 403) return false;
  const data = error?.response?.data;
  return !data || (!data.message && !data.code && data.data == null);
}

export const errorHandler = async (error) => {
  const status = error?.response?.status;
  const config = error?.config || {};
  const url = config?.url || "";
  const isAuthEndpoint = /\/auth\/(login|refresh|logout)\b/.test(url);
  const isRefreshEndpoint = /\/auth\/refresh\b/.test(url);
  const refreshEligible = status === 401 || isBodylessForbidden(error);

  // Try to rotate the access token on a single 401 (or bodyless 403) for
  // non-auth endpoints.
  if (refreshEligible && !isAuthEndpoint && !config.__isRetry) {
    const next = await refreshAccessToken();
    if (next) {
      config.__isRetry = true;
      const storeId = Auth.activeStoreId;
      config.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${next}`,
        ...(storeId != null ? { "X-Store-Id": String(storeId) } : {}),
      };
      return axios.request(config);
    }
    return Promise.reject({ ...error });
  }

  // Refresh itself failed → already handled in refreshAccessToken.
  if (status === 401 && isRefreshEndpoint) {
    return Promise.reject({ ...error });
  }

  // Surface validation/business errors via existing toast layer, except on the
  // login endpoint where the Login shell renders inline error feedback.
  if (!isAuthEndpoint) {
    if (status === 401) {
      Storage.setFlash("error_message", "Session expired, please login again.");
      Auth.logout();
    }
    // A caller may opt out of the global toast (config.__silent) when it renders
    // its own feedback or a failure is expected/non-fatal (e.g. optional lookups).
    if (!config.__silent) {
      Notification.error(error?.response?.data?.message);
    }
  }

  return Promise.reject({ ...error });
};

export const successHandler = (response) => response;
